import { getApiClient } from '../axios-config';

/**
 * Workflow API Service
 * Handles all API calls related to package workflow/kanban
 */
class WorkflowAPI {
  /**
   * Get Kanban board data for a project
   * @param {string} projectId - Project ID
   * @param {string} token - JWT authentication token
   * @param {string|null} statusStepSetId - Optional status step set ID to filter by workflow
   * @returns {Promise<Object>} Object with { statusStepSets: [], selectedStatusStepSetId, statuses: [], packages: [] }
   */
  static async getKanbanBoardData(projectId, token, statusStepSetId = null) {
    const apiClient = getApiClient(token);
    let url = `/api/workflow/kanban-board/${projectId}`;
    if (statusStepSetId) {
      url += `?statusStepSetId=${encodeURIComponent(statusStepSetId)}`;
    }
    const response = await apiClient.get(url);
    return response.data.data;
  }

  /**
   * Get tracking statuses for a project
   * @param {string} projectId - Project ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of tracking status objects
   */
  static async getTrackingStatuses(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/workflow/statuses', {
      params: { projectId },
    });
    return response.data.data.statuses || [];
  }

  /**
   * Update a package's tracking status
   * @param {string} packageId - Package ID to update
   * @param {number} trackingStatusId - New tracking status ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated package object
   */
  static async updatePackageTrackingStatus(packageId, trackingStatusId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.put(
      `/api/workflow/packages/${packageId}/tracking-status`,
      { tracking_status_id: trackingStatusId }
    );
    return response.data.data;
  }

  /**
   * Save manual resource assignments for a package
   * @param {string} packageId - Package ID
   * @param {Array<string>} employeeIds - Array of employee IDs to assign
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Result object with success flag
   */
  static async savePackageAssignments(packageId, employeeIds, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/workflow/packages/${packageId}/assignments`,
      { employeeIds }
    );
    return response.data;
  }

  /**
   * Update package swim lane details (due date, notes)
   * @param {string} packageId - Package ID
   * @param {Object} details - Object with { dueDate, notes }
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Result object with updated package
   */
  static async updatePackageDetails(packageId, details, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.put(
      `/api/workflow/packages/${packageId}/details`,
      details
    );
    return response.data.data;
  }

  /**
   * Update a package's field start date (triggers due date recalculation)
   * @param {string} packageId - Package ID
   * @param {string|null} fieldStartDate - ISO date string (YYYY-MM-DD) or null to clear
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated package with recalculated swim_lane_due_date
   */
  static async updateFieldStartDate(packageId, fieldStartDate, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.patch(
      `/api/workflow/packages/${packageId}/field-start-date`,
      { field_start_date: fieldStartDate }
    );
    return response.data.data;
  }

  /**
   * Get the full swim lane schedule for a package
   * @param {string} packageId - Package ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Schedule with swim_lanes array
   */
  static async getPackageSchedule(packageId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/workflow/packages/${packageId}/schedule`
    );
    return response.data.data;
  }

  /**
   * Get consolidated task-editor modal data for a package
   * (hours, schedule dates, status history, ACC schedule activity)
   * @param {string} packageId - Package ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { hours, dates, statusHistory, scheduleActivity }
   */
  static async getPackageEditorData(packageId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/workflow/packages/${packageId}/editor-data`
    );
    return response.data.data;
  }

  /**
   * Create or update a duration override for a package swim lane
   * @param {string} packageId - Package ID
   * @param {string} trackingStatusId - Tracking status ID
   * @param {number} overrideDurationDays - New duration in days
   * @param {string|null} notes - Optional notes
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated schedule
   */
  static async createDurationOverride(
    packageId,
    trackingStatusId,
    overrideDurationDays,
    notes,
    token
  ) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/workflow/packages/${packageId}/duration-overrides`,
      {
        tracking_status_id: trackingStatusId,
        override_duration_days: overrideDurationDays,
        notes: notes || null,
      }
    );
    return response.data.data;
  }

  /**
   * Delete a duration override for a package swim lane
   * @param {string} packageId - Package ID
   * @param {string} trackingStatusId - Tracking status ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated schedule
   */
  static async deleteDurationOverride(packageId, trackingStatusId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.delete(
      `/api/workflow/packages/${packageId}/duration-overrides/${trackingStatusId}`
    );
    return response.data.data;
  }

  /* ---- Package History Modal (Ticket #216) — read-only history fetches ---- */

  /**
   * Status change history for a package (newest first).
   * @param {string} packageId
   * @param {string} token - JWT authentication token
   * @returns {Promise<{ history: Array }>}
   */
  static async getPackageStatusHistory(packageId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/workflow/packages/${packageId}/history/status`
    );
    return response.data.data;
  }

  /**
   * Timecard detail for a package (newest first).
   * @param {string} packageId
   * @param {string} token - JWT authentication token
   * @returns {Promise<{ timecards: Array }>}
   */
  static async getPackageTimecardHistory(packageId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/workflow/packages/${packageId}/history/timecards`
    );
    return response.data.data;
  }

  /**
   * Earned-hours (percent-complete) history for a package (newest first).
   * @param {string} packageId
   * @param {string} token - JWT authentication token
   * @returns {Promise<{ history: Array }>}
   */
  static async getPackageEarnedHistory(packageId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/workflow/packages/${packageId}/history/earned`
    );
    return response.data.data;
  }

  /**
   * Budget-split history for a package (newest first).
   * @param {string} packageId
   * @param {string} token - JWT authentication token
   * @returns {Promise<{ history: Array }>}
   */
  static async getPackageBudgetSplitHistory(packageId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/workflow/packages/${packageId}/history/budget-split`
    );
    return response.data.data;
  }

  /**
   * Budget change history for a package (oldest first).
   * @param {string} packageId
   * @param {string} token - JWT authentication token
   * @returns {Promise<{ history: Array }>}
   */
  static async getPackageBudgetChangeHistory(packageId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/workflow/packages/${packageId}/history/budget-change`
    );
    return response.data.data;
  }

  /**
   * Weekly snapshots for a package (oldest first — timeline).
   * @param {string} packageId
   * @param {string} token - JWT authentication token
   * @returns {Promise<{ snapshots: Array }>}
   */
  static async getPackageSnapshotsHistory(packageId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(
      `/api/workflow/packages/${packageId}/history/snapshots`
    );
    return response.data.data;
  }
}

export default WorkflowAPI;
